/* BE shared body contents controller. FAQ is not a TOC and is never selected. */
(() => {
  'use strict';
  if (/^de(?:-|$)/i.test(document.documentElement.lang || '')) return;
  const groupSelector = '.be-stone-reading,.be-by-stone,article.be-article-v3,.be-cl,.be-origin-record';
  const linkSelector = '[data-stone-toc] a[href^="#"],[data-toc][href^="#"],[data-article-toc] a[href^="#"],[data-article-mobile-toc] a[href^="#"],.be-cl__toc a[href^="#"],.be-cl__mtoc a[href^="#"],.be-origin-record .toc a[href^="#"]';
  const faqSelector = '[data-be-faq-unified],.be-faq-codex,.bb-faq,[id="faq"]';
  const groups = new Map();
  let queued = false;
  let animation = 0;
  let lockedTarget = null;
  let lockUntil = 0;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  function targetFor(link) {
    try { return document.getElementById(decodeURIComponent(link.hash.slice(1))); }
    catch (_) { return null; }
  }
  function offset() {
    const header = document.querySelector('header.header');
    const rect = header && header.getBoundingClientRect();
    const sticky = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-height')) || 0;
    return Math.max(sticky, rect && rect.bottom > 0 ? Math.min(rect.bottom, rect.height) : 0) + 16;
  }
  function mark(group, id) {
    group.links.forEach(link => {
      const active = targetFor(link)?.id === id;
      link.classList.toggle('is-active', active);
      if (active) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
  }
  function refresh() {
    queued = false;
    const inset = offset();
    document.documentElement.style.setProperty('--be-1003-scroll-offset', `${inset}px`);
    groups.forEach((group, root) => {
      if (!root.isConnected) { groups.delete(root); return; }
      const targets = group.targets;
      if (!targets.length) return;
      let active = targets[0];
      for (const target of targets) if (target.getBoundingClientRect().top <= inset + 4) active = target;
      if (lockedTarget && performance.now() < lockUntil && targets.includes(lockedTarget)) active = lockedTarget;
      mark(group, active.id);
    });
  }
  function schedule() { if (!groups.size || queued) return; queued = true; requestAnimationFrame(refresh); }
  function scan(scope = document) {
    const roots = scope.matches?.(groupSelector) ? [scope] : [...scope.querySelectorAll(groupSelector)];
    for (const root of roots) {
      const links = [...root.querySelectorAll(linkSelector)].filter(link => !link.closest(faqSelector) && targetFor(link));
      const targets = [...new Set(links.map(targetFor))];
      if (links.length) groups.set(root, { links, targets });
    }
    schedule();
  }
  function stop() { cancelAnimationFrame(animation); animation = 0; lockedTarget = null; lockUntil = 0; }
  function scrollToTarget(target) {
    stop();
    const start = window.scrollY;
    const end = Math.max(0, Math.min(document.documentElement.scrollHeight - innerHeight, start + target.getBoundingClientRect().top - offset()));
    const distance = end - start;
    const begin = performance.now();
    lockedTarget = target;
    lockUntil = begin + 700;
    function focusTarget() {
      // Sticky headers can resize during scrolling; align against their final geometry.
      const correction = target.getBoundingClientRect().top - offset();
      if (Math.abs(correction) > 1) window.scrollBy({ top:correction, behavior:'instant' });
      const hadTabIndex = target.hasAttribute('tabindex');
      if (!hadTabIndex) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll:true });
      if (!hadTabIndex) target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once:true });
      schedule();
    }
    if (reducedMotion.matches) { window.scrollTo({ top:end, behavior:'instant' }); focusTarget(); return; }
    function frame(now) {
      const progress = Math.min(1, (now - begin) / 300);
      const eased = 1 - Math.pow(1 - progress, 3);
      window.scrollTo({ top:start + distance * eased, behavior:'instant' });
      if (progress < 1) animation = requestAnimationFrame(frame);
      else { animation = 0; focusTarget(); }
    }
    animation = requestAnimationFrame(frame);
  }
  window.BEContentsScroll = Object.freeze({ to:scrollToTarget });
  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest?.(linkSelector);
    if (!link || link.closest(faqSelector)) return;
    const root = link.closest(groupSelector), target = targetFor(link);
    if (!root || !target) return;
    event.preventDefault();
    event.stopPropagation();
    const disclosure = link.closest('details');
    if (disclosure && !disclosure.matches(faqSelector)) disclosure.open = false;
    if (history.replaceState) history.replaceState(null, '', link.hash);
    scrollToTarget(target);
  }, true);
  addEventListener('scroll', schedule, { passive:true });
  addEventListener('resize', schedule, { passive:true });
  addEventListener('wheel', stop, { passive:true });
  addEventListener('touchstart', stop, { passive:true });
  document.addEventListener('keydown', event => {
    if (['ArrowDown','ArrowUp','PageDown','PageUp','Home','End','Escape'].includes(event.key)) stop();
    if (event.key === 'Escape') {
      const toc = event.target.closest?.('[data-article-mobile-toc],details[data-stone-toc],.be-by-stone .mobile-toc,.be-cl__mtoc');
      if (toc && toc.open) { toc.open = false; toc.querySelector('summary')?.focus(); }
    }
  });
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('be:toc-ready', () => scan());
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once:true });
  else scan();
})();
